<template>
  <Container
    class="flex flex-col justify-center items-center gap-2"
    dragHandleSelector=".drag-handle"
    @drop="drop"
    @drag-start="dragOngoing = true"
    @drag-end="dragOngoing = false"
  >
    <Draggable v-for="(trans, id) in modelValue" :key="id">
      <div class="flex justify-center items-center gap-4">
        <div
          class="drag-handle cursor-pointer w-6 h-10 flex justify-center items-center"
        >
          <Icon icon="ic:baseline-drag-indicator" />
        </div>

        <div class="tooltip" data-tip="Type of color interpolation">
          <ListSelect
            class="w-[14ch]"
            :clearable="false"
            :options="INTERPOLATION"
            v-model="trans.interpolation"
          />
        </div>

        <div
          class="tooltip"
          data-tip="Duration in milliseconds (0 means forever)"
        >
          <div class="join">
            <IntegerInput
              class="w-[9ch] input-sm join-item h-10"
              :min="0"
              :max="65535"
              v-model="trans.duration"
            />
            <span class="join-item btn-input-unit btn-input-unit-sm !h-10"
              >ms</span
            >
          </div>
        </div>

        <ColorInput v-model="trans.color" />

        <button
          class="btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral bg-base-100 hover:border-neutral hover:bg-neutral"
          :class="
            modelValue.length <= 1 ? 'pointer-events-none opacity-40' : ''
          "
          @click="() => remove(id)"
        >
          <Icon icon="ic:baseline-delete" @mouseout.stop />
        </button>
      </div>
    </Draggable>

    <button
      v-if="modelValue.length < 254"
      class="btn btn-circle btn-sm btn-outline h-9 w-9 text-neutral hover:border-neutral hover:bg-neutral"
      @click="add"
    >
      <Icon icon="ic:baseline-add" class="hover:fill-neutral-content" />
    </button>
  </Container>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Container, Draggable, DropResult } from "vue-dndrop";
import { Transition } from "@/keyboard";
import { INTERPOLATION } from "@/keyboard/leds";
import IntegerInput from "@/components/utils/IntegerInput.vue";
import ColorInput from "@/components/utils/ColorInput.vue";
import ListSelect from "@/components/utils/ListSelect.vue";

const props = defineProps<{
  modelValue: Transition[];
}>();

const emit = defineEmits<{
  (_e: "update:modelValue", _value: Transition[]): void;
}>();

const dragOngoing = ref(false);

const remove = (id: number) => {
  if (id < 0 || id >= props.modelValue.length) return;
  const tnew = props.modelValue
    .slice(0, id)
    .concat(props.modelValue.slice(id + 1));
  emit("update:modelValue", tnew);
};

const add = () => {
  const added: Transition = {
    color: [255, 255, 255],
    duration: 1000,
    interpolation: "Linear",
  };
  emit("update:modelValue", [...props.modelValue, added]);
};

const drop = (result: DropResult) => {
  const from = result.removedIndex;
  const to = result.addedIndex;
  if (from == null || to == null) return;

  const list = [...props.modelValue];
  if (from < 0 || from > list.length - 1) return; // out of range
  if (to < 0 || to > list.length - 1) return; // out of range

  list.splice(to, 0, list.splice(from, 1)[0]);

  emit("update:modelValue", list);
};
</script>

<style scoped>
/* Make the "delete" buttons visible */
.dndrop-container.vertical > .dndrop-draggable-wrapper {
  @apply overflow-visible;
}
</style>
